import { ApiError } from "@/api/client";

/**
 * Meldung eines fehlgeschlagenen Aufrufs — der Satz des SERVERS, wenn es einen gibt.
 *
 * Hintergrund (G2, P10 `a430d8cd`): das Backend beantwortet fachliche Sperren mit einem
 * Status UND einem Satz, der den Grund nennt („Ohne Lieferant kann nicht bestellt werden",
 * „Datei zu gross (max. 25 MB).", „PDF wird noch nicht ausgewertet…"). Wer diesen Satz
 * wegwirft und einen festen Text zeigt, schickt den Nutzer an die falsche Stelle — die
 * Akte tat das beim Statuswechsel sogar mit der Regelnummer **E17**, obwohl eine ANDERE
 * Regel gegriffen hatte.
 *
 * Der Rueckfall bleibt trotzdem noetig, denn `ApiError.message` ist NICHT immer ein Satz
 * vom Server. Fehlt im Fehlerkoerper die Meldung, setzt der Client `HTTP <code>` ein; ist
 * die Antwort gar nicht lesbar (Proxy-Fehlerseite statt JSON), `HTTP <code> (Antwort nicht
 * lesbar)` mit `undecodable`. Solche Platzhalter anzuzeigen waere ein Rueckschritt — dort
 * ist der eigene, verstaendliche Satz die bessere Auskunft. Deshalb prueft diese Funktion
 * den INHALT und reicht nicht blind `e.message` durch.
 *
 * Gebraucht in der Akte (Lieferant/Status), im Assistenten (Datei-Auswertung) und auf der
 * Reichweiten-Seite (fehlgeschlagene Abfrage) — darum EINE Stelle statt dreimal derselbe
 * Gedanke. Seit 2026-08-18 unter `lib/`, weil der dritte Nutzer kein Einkaufs-Bildschirm
 * mehr ist; ein Import aus `pages/analytics` nach `pages/purchase` waere eine Abhaengigkeit
 * zwischen zwei Bildschirmen gewesen, die nichts miteinander zu tun haben.
 */
export function fehlerText(e: unknown, rueckfall: string): string {
  if (!(e instanceof ApiError) || e.undecodable) return rueckfall;
  const satz = e.message.trim();
  if (satz === "" || /^HTTP \d+/.test(satz)) return rueckfall;
  return satz;
}

/**
 * Ist dieser Satz in Wahrheit ein technischer Platzhalter?
 *
 * Der Datei-Helfer `downloadPdf` (`api/warehouse.ts`) liefert bei einer nicht lesbaren
 * Antwort — Proxy-Fehlerseite statt JSON — den Text „Download fehlgeschlagen (HTTP 502)".
 * Das ist derselbe Fall, den `fehlerText` beim JSON-Weg abfaengt, nur mit anderem Wortlaut:
 * eine Zahl, die dem Nutzer nichts sagt. Wer den Satz eines Servers anzeigen will, muss
 * diesen Rueckfall des Helfers davon unterscheiden koennen. (Befund beider Reviewer.)
 */
export function istTransportmeldung(satz: string): boolean {
  return /\(HTTP \d+\)/.test(satz) || /^HTTP \d+/.test(satz);
}

/**
 * Status eines fachlich beantworteten Fehlers — oder `null`, wenn der Ausgang unklar ist.
 *
 * Wird gebraucht, um „der Server hat geantwortet und sagt, was zu tun ist" (413/415/422/501)
 * von „wir wissen nicht, was passiert ist" (Netzfehler, Proxy-Seite, 5xx) zu trennen. Ein
 * `undecodable` zaehlt bewusst NICHT als fachliche Antwort: wir haben den Fehlerkoerper nie
 * gesehen, der blosse Status traegt die Aussage nicht (`isTransportError`-Begruendung im
 * Client).
 */
export function fachlicherStatus(e: unknown): number | null {
  if (!(e instanceof ApiError) || e.undecodable) return null;
  return e.status;
}
